<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ProductCategoriesAPI from 'dashboard/api/productCategories';
import WebsiteLayout from '../components/WebsiteLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ProductCategoryForm from '../components/ProductCategoryForm.vue';
import TablePagination from '../components/TablePagination.vue';
import ResponsiveTable from '../components/ResponsiveTable.vue';

const { t } = useI18n();

const categories = ref([]);
const loading = ref(false);
const showForm = ref(false);
const selectedCategory = ref(null);

// Header controls
const searchQuery = ref('');
const showSortMenu = ref(false);
const sortField = ref(null);
const sortOrder = ref('asc');

// Pagination
const currentPage = ref(1);
const itemsPerPage = ref(25);

const headerTitle = computed(() =>
  t('WEBSITE.HEADER.PRODUCT_CATEGORIES_TITLE')
);

// Filtrage et tri
const filteredAndSortedCategories = computed(() => {
  let result = [...categories.value];

  // Recherche
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    result = result.filter(
      category =>
        category.name.toLowerCase().includes(query) ||
        category.description?.toLowerCase().includes(query)
    );
  }

  // Tri
  if (sortField.value) {
    result.sort((a, b) => {
      let aVal = a[sortField.value];
      let bVal = b[sortField.value];

      if (
        sortField.value === 'updated_at' ||
        sortField.value === 'created_at'
      ) {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortOrder.value === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder.value === 'asc' ? 1 : -1;
      return 0;
    });
  }

  return result;
});

// Pagination
const startIndex = computed(() => (currentPage.value - 1) * itemsPerPage.value);

const endIndex = computed(() =>
  Math.min(
    startIndex.value + itemsPerPage.value,
    filteredAndSortedCategories.value.length
  )
);

// Résultats paginés
const paginatedCategories = computed(() => {
  return filteredAndSortedCategories.value.slice(
    startIndex.value,
    endIndex.value
  );
});

// États
const isEmpty = computed(() => !loading.value && categories.value.length === 0);

const hasNoSearchResults = computed(
  () =>
    !loading.value &&
    categories.value.length > 0 &&
    filteredAndSortedCategories.value.length === 0 &&
    searchQuery.value
);

// Reset pagination when filters change
watch([searchQuery, sortField, itemsPerPage], () => {
  currentPage.value = 1;
});

const fetchCategories = async () => {
  loading.value = true;
  try {
    const response = await ProductCategoriesAPI.get();
    categories.value = response.data;
  } catch (error) {
    useAlert(t('WEBSITE.PRODUCT_CATEGORIES.API.ERROR.FETCH_FAILED'));
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  fetchCategories();
});

const openCreateForm = () => {
  selectedCategory.value = null;
  showForm.value = true;
};

const openEditForm = category => {
  selectedCategory.value = category;
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  selectedCategory.value = null;
};

const handleSave = async () => {
  await fetchCategories();
  closeForm();
};

const deleteCategory = async category => {
  // eslint-disable-next-line no-alert, no-restricted-globals
  if (!confirm(t('WEBSITE.PRODUCT_CATEGORIES.CONFIRM.DELETE'))) return;

  try {
    await ProductCategoriesAPI.delete(category.id);
    useAlert(t('WEBSITE.PRODUCT_CATEGORIES.API.SUCCESS.CATEGORY_DELETED'));
    await fetchCategories();
  } catch (error) {
    useAlert(t('WEBSITE.PRODUCT_CATEGORIES.API.ERROR.DELETE_FAILED'));
  }
};

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString();
};

// Fermer tous les menus sauf celui spécifié
const closeAllMenusExcept = exceptMenu => {
  if (exceptMenu !== 'sort') showSortMenu.value = false;
};

// Gestion tri
const handleSort = field => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = field;
    sortOrder.value = 'asc';
  }
  showSortMenu.value = false;
};

const clearSearch = () => {
  searchQuery.value = '';
  sortField.value = null;
  sortOrder.value = 'asc';
  currentPage.value = 1;
};
</script>

<template>
  <WebsiteLayout :header-title="headerTitle">
    <template #actions>
      <Button
        :label="t('WEBSITE.PRODUCT_CATEGORIES.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </template>

    <!-- Loading -->
    <div
      v-if="loading"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-24 rounded-lg bg-gradient-to-br from-n-solid-1 to-n-slate-2 dark:to-n-slate-1 border-2 border-dashed border-n-weak"
    >
      <div
        class="mb-6 w-20 h-20 rounded-full bg-n-purple-2 dark:bg-n-purple-3 flex items-center justify-center"
      >
        <span class="i-lucide-tags size-10 text-n-purple-10" />
      </div>
      <h3 class="text-lg font-semibold text-n-slate-12 mb-2">
        {{ t('WEBSITE.PRODUCT_CATEGORIES.EMPTY_STATE.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.PRODUCT_CATEGORIES.EMPTY_STATE.SUBTITLE') }}
      </p>
      <Button
        :label="t('WEBSITE.PRODUCT_CATEGORIES.EMPTY_STATE.BUTTON_LABEL')"
        icon="i-lucide-plus"
        size="sm"
        @click="openCreateForm"
      />
    </div>

    <!-- Header bar avec search, tri -->
    <div
      v-else-if="!isEmpty"
      class="mb-3 bg-n-solid-1 border border-n-weak/70 rounded-lg shadow-sm"
    >
      <div class="flex items-center gap-2 px-2.5 py-1.5">
        <!-- Search bar -->
        <div class="flex-1 max-w-sm">
          <Input
            v-model="searchQuery"
            type="search"
            :placeholder="
              t('WEBSITE.PRODUCT_CATEGORIES.LIST.SEARCH_PLACEHOLDER')
            "
            :custom-input-class="[
              'h-8 [&:not(.focus)]:!border-transparent [&:not(.focus)]:!outline-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8 !text-xs',
            ]"
          >
            <template #prefix>
              <Icon
                icon="i-lucide-search"
                class="absolute -translate-y-1/2 text-n-slate-11 size-3.5 top-1/2 ltr:left-2 rtl:right-2 pointer-events-none"
              />
            </template>
          </Input>
        </div>

        <!-- Séparateur vertical -->
        <div class="h-6 w-px bg-n-weak/40" />

        <!-- Tri -->
        <div class="relative">
          <button
            class="px-2.5 py-1.5 bg-n-solid-1 border border-n-weak/50 text-n-slate-11 text-xs hover:bg-n-blue-2 hover:text-n-blue-11 hover:border-n-blue-6 transition-all flex items-center gap-1.5 rounded-lg font-medium"
            :class="{
              'bg-n-blue-3 text-n-blue-11 border-n-blue-6': sortField,
            }"
            @click="
              closeAllMenusExcept('sort');
              showSortMenu = !showSortMenu;
            "
          >
            <span class="i-lucide-arrow-up-down size-3.5" />
            <span>{{ t('WEBSITE.PRODUCT_CATEGORIES.LIST.SORT') }}</span>
            <span
              v-if="sortField"
              class="w-1.5 h-1.5 bg-n-blue-9 rounded-full animate-pulse"
            />
          </button>

          <div
            v-if="showSortMenu"
            v-on-clickaway="() => (showSortMenu = false)"
            class="absolute right-0 mt-1.5 w-60 bg-n-solid-1 border border-n-weak/60 shadow-2xl rounded-xl z-[100] py-2.5 overflow-hidden"
          >
            <button
              class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
              :class="{
                'bg-n-blue-2 text-n-blue-11': sortField === 'name',
              }"
              @click="handleSort('name')"
            >
              <span class="flex items-center gap-2.5 font-medium">
                <span
                  class="i-lucide-type size-3.5 group-hover:scale-110 transition-transform"
                />
                <span>{{
                  t('WEBSITE.PRODUCT_CATEGORIES.LIST.SORT_NAME')
                }}</span>
              </span>
              <span
                v-if="sortField === 'name'"
                :class="
                  sortOrder === 'asc'
                    ? 'i-lucide-arrow-up'
                    : 'i-lucide-arrow-down'
                "
                class="size-3.5 text-n-blue-11"
              />
            </button>
            <button
              class="w-full px-4 py-3 text-left text-xs hover:bg-n-alpha-2 transition-all duration-150 flex items-center justify-between group"
              :class="{
                'bg-n-blue-2 text-n-blue-11': sortField === 'updated_at',
              }"
              @click="handleSort('updated_at')"
            >
              <span class="flex items-center gap-2.5 font-medium">
                <span
                  class="i-lucide-calendar size-3.5 group-hover:scale-110 transition-transform"
                />
                <span>{{
                  t('WEBSITE.PRODUCT_CATEGORIES.LIST.SORT_DATE')
                }}</span>
              </span>
              <span
                v-if="sortField === 'updated_at'"
                :class="
                  sortOrder === 'asc'
                    ? 'i-lucide-arrow-up'
                    : 'i-lucide-arrow-down'
                "
                class="size-3.5 text-n-blue-11"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- No search results -->
    <div
      v-if="hasNoSearchResults"
      class="flex flex-col items-center justify-center py-16 bg-n-solid-1 border border-n-weak/70 rounded-lg"
    >
      <span class="i-lucide-search-x mb-4 text-n-slate-9 size-12" />
      <h3 class="text-base font-semibold text-center text-n-slate-12 mb-2">
        {{ t('WEBSITE.PRODUCT_CATEGORIES.NO_RESULTS.TITLE') }}
      </h3>
      <p class="text-sm text-center text-n-slate-10 max-w-md mb-6">
        {{ t('WEBSITE.PRODUCT_CATEGORIES.NO_RESULTS.SUBTITLE') }}
      </p>
      <Button
        :label="t('WEBSITE.PRODUCT_CATEGORIES.NO_RESULTS.CLEAR_BUTTON')"
        icon="i-lucide-x"
        size="sm"
        variant="outline"
        color="slate"
        @click="clearSearch"
      />
    </div>

    <!-- Categories list -->
    <div
      v-else-if="!isEmpty && !hasNoSearchResults"
      class="overflow-hidden border rounded-lg border-n-weak/70 bg-n-solid-1 shadow-sm min-h-[400px]"
    >
      <ResponsiveTable>
        <table class="w-full">
          <thead class="bg-n-alpha-1/50 border-b border-n-weak/60">
            <tr>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-tags size-3.5 opacity-40" />
                  <span>{{ t('WEBSITE.PRODUCT_CATEGORIES.LIST.NAME') }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-file-text size-3.5 opacity-40" />
                  <span>{{
                    t('WEBSITE.PRODUCT_CATEGORIES.LIST.DESCRIPTION')
                  }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-factory size-3.5 opacity-40" />
                  <span>{{
                    t('WEBSITE.PRODUCT_CATEGORIES.LIST.MANUFACTURER')
                  }}</span>
                </div>
              </th>
              <th
                class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
              >
                <div class="flex items-center gap-1.5">
                  <span class="i-lucide-calendar size-3.5 opacity-40" />
                  <span>{{
                    t('WEBSITE.PRODUCT_CATEGORIES.LIST.UPDATED')
                  }}</span>
                </div>
              </th>
              <th
                class="sticky right-0 px-5 py-3 text-xs font-semibold text-right tracking-wide text-n-slate-11 bg-n-alpha-1/50 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
              >
                <div class="flex items-center justify-end gap-1.5">
                  <span>{{
                    t('WEBSITE.PRODUCT_CATEGORIES.LIST.ACTIONS')
                  }}</span>
                  <span class="i-lucide-more-horizontal size-3.5 opacity-30" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody class="bg-n-solid-1">
            <tr
              v-for="category in paginatedCategories"
              :key="category.id"
              class="transition-all duration-150 ease-out border-t border-n-weak/40 hover:bg-n-blue-2/50 group"
            >
              <td class="px-5 py-3">
                <div class="flex items-center gap-3">
                  <div
                    class="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0"
                    :class="[
                      category.logo
                        ? 'bg-n-solid-1 border border-n-weak'
                        : 'bg-n-purple-2',
                    ]"
                  >
                    <img
                      v-if="category.logo"
                      :src="category.logo.url"
                      :alt="category.name"
                      class="w-full h-full object-cover"
                    />
                    <span v-else class="i-lucide-tag size-5 text-n-purple-10" />
                  </div>
                  <div class="min-w-0">
                    <span class="text-sm font-medium text-n-slate-12 block">
                      {{ category.name }}
                    </span>
                  </div>
                </div>
              </td>
              <td class="px-5 py-3">
                <p class="text-sm text-n-slate-10 line-clamp-2 max-w-md">
                  {{ category.description || '-' }}
                </p>
              </td>
              <td class="px-5 py-3">
                <span
                  v-if="category.manufacturer"
                  class="text-sm text-n-slate-12"
                >
                  {{ category.manufacturer.name }}
                </span>
                <span v-else class="text-sm text-n-slate-10">-</span>
              </td>
              <td class="px-5 py-3 text-sm text-n-slate-10">
                {{ formatDate(category.updated_at) }}
              </td>
              <td
                class="sticky right-0 px-5 py-3 bg-n-solid-1 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]"
              >
                <div class="flex justify-end gap-2">
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-blue-3 hover:bg-n-blue-4 text-n-blue-11 hover:text-n-blue-12 border border-n-blue-6 hover:border-n-blue-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    :title="t('WEBSITE.PRODUCT_CATEGORIES.FORM.EDIT')"
                    @click="openEditForm(category)"
                  >
                    <span class="i-lucide-edit size-4" />
                  </button>
                  <button
                    class="flex items-center justify-center w-8 h-8 rounded-md bg-n-ruby-3 hover:bg-n-ruby-4 text-n-ruby-11 hover:text-n-ruby-12 border border-n-ruby-6 hover:border-n-ruby-7 transition-all duration-200 shadow-sm hover:shadow-md"
                    :title="t('WEBSITE.PRODUCT_CATEGORIES.FORM.DELETE')"
                    @click="deleteCategory(category)"
                  >
                    <span class="i-lucide-trash-2 size-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </ResponsiveTable>
    </div>

    <!-- Pagination Footer -->
    <div
      v-if="
        !isEmpty &&
        !hasNoSearchResults &&
        filteredAndSortedCategories.length > 0
      "
      class="mt-6"
    >
      <TablePagination
        :current-page="currentPage"
        :total-items="filteredAndSortedCategories.length"
        :items-per-page="itemsPerPage"
        @update:current-page="currentPage = $event"
        @update:items-per-page="itemsPerPage = $event"
      />
    </div>

    <!-- Category Form Drawer -->
    <ProductCategoryForm
      v-if="showForm"
      :category="selectedCategory"
      @close="closeForm"
      @save="handleSave"
    />
  </WebsiteLayout>
</template>
